/**
 * The client record.
 *
 * A client is an ongoing relationship, not a contact card and not a matter: it persists
 * across many matters and beyond the closure of any of them. So this screen is arranged
 * around the relationship — who acts for them, what they can see, what they have
 * consented to, and what work is on the file.
 *
 * The detail payload nests `contacts`, so the summary and contacts sections need no
 * second request. `_actions` on the payload is the accurate permission answer for THIS
 * client, scope included, and every mutating control below is gated on it rather than
 * on a bare capability check.
 *
 * A 404 here is "absent as far as you are concerned". It covers a client that does not
 * exist, one belonging to another firm, and one outside this reader's scope,
 * deliberately indistinguishably. `ErrorState` knows not to say anything about
 * permissions for a 404 and this screen adds nothing to it.
 */

import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type ClientDetail,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  ErrorState,
  Skeleton,
  StatusPill,
  TabPanel,
  Tabs,
  useFieldId,
} from '@legalcrm/ui';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Can } from '@/auth/permissions';
import { DeleteWithReasonDialog } from '@/components/DeleteWithReasonDialog';
import { humanise } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

import { ClientAgreementsTab } from './ClientAgreementsTab';
import { ClientConsentsTab } from './ClientConsentsTab';
import { ClientContactsTab } from './ClientContactsTab';
import { ClientMattersTab } from './ClientMattersTab';
import { ClientPortalAccessTab } from './ClientPortalAccessTab';
import { ClientSummaryTab } from './ClientSummaryTab';

type TabKey = 'summary' | 'contacts' | 'agreements' | 'portal' | 'consents' | 'matters';

/** A client's status is its own vocabulary, not one `statusTone` already knows. */
export const CLIENT_STATUS_TONES = {
  prospective: 'info',
  active: 'success',
  dormant: 'warning',
  archived: 'neutral',
  closed: 'neutral',
} as const;

const TAB_KEYS: readonly TabKey[] = [
  'summary',
  'contacts',
  'agreements',
  'portal',
  'consents',
  'matters',
];

function tabFrom(value: string | null): TabKey {
  return TAB_KEYS.find((key) => key === value) ?? 'summary';
}

export function ClientDetailPage() {
  const { clientId = '' } = useParams();
  const baseId = useFieldId();
  const api = useApi();
  const navigate = useNavigate();
  // `?tab=portal` is how "the client cannot see this matter" lands on the screen that
  // fixes it, rather than on a summary the reader then has to leave.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<TabKey>(() => tabFrom(searchParams.get('tab')));
  const [deleting, setDeleting] = useState(false);

  const query = useApiQuery<ClientDetail>(
    qk.detail('clients', clientId),
    STAFF.client(clientId),
    { enabled: Boolean(clientId) },
  );

  // Above the early returns, so the hook order never changes between renders.
  const remove = useApiMutation<unknown, string>(
    {
      mutationFn: (reason) => api.delete(STAFF.client(clientId), { body: { reason } }),
      invalidates: [qk.all('clients'), qk.dashboard()],
    },
    { onSuccess: () => navigate('/clients') },
  );

  if (query.isLoading) return <ClientSkeleton />;

  if (query.error || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const client = query.data;
  const liveContacts = client.contacts.length;

  const tabs = [
    { key: 'summary', label: 'Summary' },
    { key: 'contacts', label: 'Contacts', badge: liveContacts || undefined },
    { key: 'agreements', label: 'Agreements' },
    { key: 'portal', label: 'Portal access' },
    { key: 'consents', label: 'Consents' },
    { key: 'matters', label: 'Matters' },
  ];

  return (
    <>
      <PageHeader
        title={client.display_name}
        description={
          client.client_type === 'organization'
            ? 'Organisation'
            : 'Individual'
        }
        meta={
          <>
            <span className="font-mono text-sm text-ink-muted">{client.reference}</span>
            <StatusPill
              label={humanise(client.status)}
              tone={CLIENT_STATUS_TONES[client.status] ?? 'neutral'}
            />
            {/* The portal being enabled is a PRECONDITION for access, never a grant of
                it. Labelled in words so it cannot be read as "this client can see
                everything". */}
            <Badge tone={client.portal_enabled ? 'info' : 'neutral'} size="sm">
              {client.portal_enabled ? 'Portal opened' : 'Portal not opened'}
            </Badge>
            {client.origin_lead_id && (
              <Badge tone="neutral" size="sm">
                Converted from a lead
              </Badge>
            )}
          </>
        }
        actions={
          <Can module="client" action="delete">
            <Button variant="ghost" onClick={() => setDeleting(true)}>
              Delete
            </Button>
          </Can>
        }
      />

      <DeleteWithReasonDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        label={client.reference}
        noun="client"
        caveat="A client whose work is simply finished becomes dormant on its own — this is for a record that should not exist."
        busy={remove.isPending}
        error={remove.error}
        onConfirm={(reason) => remove.mutateAsync(reason)}
      />

      <Tabs
        tabs={tabs}
        value={tab}
        onChange={(key) => setTab(key as TabKey)}
        label="Client sections"
        className="mb-4"
      />

      {tab === 'summary' && (
        <TabPanel baseId={baseId} tabKey="summary">
          <ClientSummaryTab client={client} />
        </TabPanel>
      )}

      {tab === 'contacts' && (
        <TabPanel baseId={baseId} tabKey="contacts">
          <ClientContactsTab client={client} />
        </TabPanel>
      )}

      {tab === 'agreements' && (
        <TabPanel baseId={baseId} tabKey="agreements">
          <ClientAgreementsTab client={client} />
        </TabPanel>
      )}

      {tab === 'portal' && (
        <TabPanel baseId={baseId} tabKey="portal">
          <ClientPortalAccessTab client={client} />
        </TabPanel>
      )}

      {tab === 'consents' && (
        <TabPanel baseId={baseId} tabKey="consents">
          <ClientConsentsTab client={client} />
        </TabPanel>
      )}

      {tab === 'matters' && (
        <TabPanel baseId={baseId} tabKey="matters">
          <ClientMattersTab client={client} />
        </TabPanel>
      )}
    </>
  );
}

function ClientSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <span className="sr-only">Loading client</span>
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-9 w-full" />
      <div className="grid gap-4 lg:grid-cols-3">
        <Skeleton className="h-64 lg:col-span-2" />
        <Skeleton className="h-64" />
      </div>
    </div>
  );
}
